import { noop } from '@sgme/fp';
import classnames from 'classnames';
import { SpDropdownToggle } from 'components/shared/Dropdown';
import { FormattedMessage } from 'components/shared/Intl';
import { FC, ReactNode } from 'react';
import { Dropdown, DropdownItem, DropdownMenu } from 'react-bootstrap';

import type { IInputPanelProps, SelectingStatus } from '../Typeahead.models';
import { InputPanel } from './InputPanel';
import { StringResultPanel } from './StringResultPanel';

export type TypeaheadPresentationProps<TItem> = IInputPanelProps & SelectingStatus<TItem> & { className?: string };

export const LocalizedTypeaheadPresentation: FC<TypeaheadPresentationProps<any>> = props => {
  const displayNoDataAvailable =
    props.selecting && !props.fetchingResults && !(props.availableValues && props.availableValues.length);
  return (
    <div className={classnames('typeahead-component', props.className)} title={props.searchText}>
      <InputPanel
        searchText={props.searchText}
        placeholder={props.placeholder}
        isDisabled={props.isDisabled}
        required={props.required}
        locked={props.locked}
        isLoading={props.isLoading}
        hasError={props.hasError}
        displaySpinner={props.displaySpinner}
        fetchingResults={props.fetchingResults}
        id={props.id}
        e2e={props.e2e}
        name={props.name}
        tabIndex={props.tabIndex}
        size={props.size}
        onInputChange={props.onInputChange}
        onInputKeyDown={props.onInputKeyDown}
        onInputFocus={props.onInputFocus}
        onInputBlur={props.onInputBlur}
        isCellInactive={props.isCellInactive}
        isFocused={props.isFocused}
      />

      <Dropdown show={props.selecting} onToggle={noop}>
        <SpDropdownToggle
          variant="link"
          caret={false}
          className="border-0 p-0 btn-link w-100"
          color="default"
          tabIndex={-1}
        />

        <DropdownMenu>
          {getResultPanel(props)}
          {displayNoDataAvailable && (
            <span className="noResultFound">
              <DropdownItem disabled>
                <FormattedMessage id="noDataAvailable" />
              </DropdownItem>
            </span>
          )}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

LocalizedTypeaheadPresentation.displayName = 'TypeaheadPresentation';

function getResultPanel(props: TypeaheadPresentationProps<any>): ReactNode {
  if (!props.selecting || props.fetchingResults || !props.availableValues || !props.availableValues.length) {
    return null;
  }
  if (props.children === undefined) {
    return <StringResultPanel {...props} />;
  }
  return props.children({ ...props });
}

export const TypeaheadPresentation = LocalizedTypeaheadPresentation;
